// The client app's Cognito sign-in (TO-1696). The app calls the user pool API from the browser on
// the public client-ui app client: no hosted pages, no SDK (a few JSON calls), no client secret.
// Cognito is on only when both build variables are set; without them every screen works as before.
//
// Only the access token is kept (for a password change, 30 minutes); our API token from
// POST /login/cognito is still the session.

const POOL_ID = process.env.REACT_APP_COGNITO_POOL_ID || "";
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const ACCESS_TOKEN_KEY = "cognitoAccessToken";

export const cognitoEnabled = () => POOL_ID !== "" && CLIENT_ID !== "";

const endpoint = () => `https://cognito-idp.${POOL_ID.split("_")[0]}.amazonaws.com/`;

export class CognitoError extends Error {
    constructor(code, message) {
        super(message || code);
        this.code = code;
    }
}

const call = async (operation, body) => {
    let response;
    try {
        response = await fetch(endpoint(), {
            method: "POST",
            headers: {
                "Content-Type": "application/x-amz-json-1.1",
                "X-Amz-Target": `AWSCognitoIdentityProviderService.${operation}`
            },
            body: JSON.stringify(body)
        });
    } catch (error) {
        throw new CognitoError("NetworkError", "We couldn't reach the sign-in service. Please check your connection and try again.");
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        // __type is "NotAuthorizedException" or "com.amazonaws...#NotAuthorizedException"
        const code = String(data.__type || "UnknownError").split("#").pop();
        throw new CognitoError(code, data.message);
    }
    return data;
};

/**
 * What the person typed, as Cognito expects it: a US phone number as E.164 (+1...), anything else
 * (an email or a legacy user name) trimmed.
 */
export const toUsername = (typed) => {
    const value = (typed || "").trim();
    if (value.includes("@")) return value;
    const digits = value.replace(/[\s().-]/g, "");
    if (/^\+\d{11,15}$/.test(digits)) return digits;
    if (/^\d{10}$/.test(digits)) return `+1${digits}`;
    if (/^1\d{10}$/.test(digits)) return `+${digits}`;
    return value;
};

/** Password sign-in. Resolves to the ID token; throws CognitoError (see messageFor). */
export const signIn = async (typed, password) => {
    const data = await call("InitiateAuth", {
        AuthFlow: "USER_PASSWORD_AUTH",
        ClientId: CLIENT_ID,
        AuthParameters: {USERNAME: toUsername(typed), PASSWORD: password}
    });
    if (!data.AuthenticationResult) {
        // An MFA or new-password challenge: not offered to patients yet
        throw new CognitoError("UnsupportedChallenge", data.ChallengeName);
    }
    try {
        sessionStorage.setItem(ACCESS_TOKEN_KEY, data.AuthenticationResult.AccessToken);
    } catch (e) { /* storage blocked: only a password change needs it */ }
    return data.AuthenticationResult.IdToken;
};

/** Sends a reset code. Resolves to the delivery medium ("EMAIL"); throws CognitoError. */
export const sendResetCode = async (typed) => {
    const data = await call("ForgotPassword", {ClientId: CLIENT_ID, Username: toUsername(typed)});
    const medium = data.CodeDeliveryDetails?.DeliveryMedium;
    // Decision 2 (16234): resets go by email only; a phone-only person contacts support
    if (medium !== "EMAIL") throw new CognitoError("NoEmail");
    return medium;
};

export const confirmReset = (typed, code, password) =>
    call("ConfirmForgotPassword", {ClientId: CLIENT_ID, Username: toUsername(typed), ConfirmationCode: code.trim(), Password: password});

/** Changes the signed-in person's password. Cognito then ends their sessions (the events trigger). */
export const changePassword = (previousPassword, proposedPassword) => {
    let accessToken = null;
    try { accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY); } catch (e) { /* see below */ }
    if (!accessToken) return Promise.reject(new CognitoError("SignInAgain"));
    return call("ChangePassword", {AccessToken: accessToken, PreviousPassword: previousPassword, ProposedPassword: proposedPassword});
};

export const forgetCognitoSession = () => {
    try { sessionStorage.removeItem(ACCESS_TOKEN_KEY); } catch (e) { /* nothing kept */ }
};

const SUPPORT = "Please contact support.";

/** What to tell the person for a CognitoError. */
export const messageFor = (error) => {
    switch (error?.code) {
        case "NotAuthorizedException":
            if (/attempts exceeded/i.test(error.message)) return "Too many failed sign-in attempts. Your account is locked for a while. Try again later, or reset your password.";
            if (/Access Token/i.test(error.message)) return "Please sign in again to change your password.";
            if (/disabled/i.test(error.message)) return `This account is turned off. ${SUPPORT}`;
            return "We are sorry. The username or password you used is incorrect.";
        case "UserNotFoundException":
            return "We are sorry. The username or password you used is incorrect.";
        case "PasswordResetRequiredException":
            return "Please set a new password of at least 12 characters. We'll email you a code.";
        case "CodeMismatchException":
            return "That code is not correct. Please check your email and try again.";
        case "ExpiredCodeException":
            return "That code has expired. Please ask for a new one.";
        case "InvalidPasswordException":
            return "Your new password must be at least 12 characters long.";
        case "LimitExceededException":
        case "TooManyRequestsException":
        case "TooManyFailedAttemptsException":
            return "Too many attempts. Please wait a few minutes and try again.";
        case "NoEmail":
            return `Password reset needs an email address on your account. ${SUPPORT}`;
        case "InvalidParameterException":
            return /verified email|registered\/verified/i.test(error.message)
                ? `Password reset needs an email address on your account. ${SUPPORT}`
                : "Something went wrong. Please try again.";
        case "SignInAgain":
            return "Please sign in again to change your password.";
        case "UnsupportedChallenge":
            return `This account needs a sign-in step the app doesn't offer yet. ${SUPPORT}`;
        case "NetworkError":
            return error.message;
        default:
            return "Something went wrong. Please try again.";
    }
};
